import { useContext, useEffect, useState } from "react";
import { userState } from "../../state/UserState";
import { PhonePreview } from "../../components/PhonePreview";
import {
  getTierOptions,
  scheduleNotification,
  sendCustomFlashMessage,
} from "../../api/new-api";
import { ErrorAlert } from "../../components/alerts";
import { PEProgressBar } from "../../components/linear-progress";
import { SCHEDULE_NOTIFICATIONS } from "./player-communications-page";
import { useColors } from "../../provider/colors-provider";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import CustomTextarea from "../../components/CustomTextarea";
import { observer } from "mobx-react";
import { UserStore } from "../..";
import { Alert, Snackbar } from "@mui/material";

type Props = {
  scheduleType: string;
  notificationScheduleTime: Date;
  refetch: () => void;
};
const CustomFilterNotification = ({
  scheduleType,
  notificationScheduleTime,
  refetch,
}: Props) => {
  const userStore = useContext(UserStore);
  const { primary } = useColors();
  const [tierOptions, setTierOptions] = useState<string[]>([]);
  const [tierOption, setTierOption] = useState<string>("Select Tier");
  const [notification, setNotification] = useState<any>({
    title: null,
    message: null,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const handleFetchTiers = async () => {
    const res = await getTierOptions();
    if (res?.tierOptions) {
      setTierOptions(res?.tierOptions);
    }
    setLoading(false);
  };

  const validate = () => {
    const { title, message } = notification;

    if (tierOption === "Select Tier" || !tierOption) {
      setError("Please select tier option");
      return;
    }
    if (title === "" || message === "" || !title || !message) {
      setError("Title and message are required");
      return false;
    }
    return true;
  };

  const sendToVenue = async () => {
    setLoading(true);
    setError("");
    try {
      if (scheduleType === SCHEDULE_NOTIFICATIONS.NOW) {
        await sendCustomFlashMessage({
          tierOption,
          ...notification,
        });
        setTierOption("Select Tier");
        setNotification({
          title: null,
          message: null,
        });
        setSuccessMessage(
          `Success: Notification sent to ${
            tierOption === "No Tier"
              ? "No Tier members"
              : tierOption !== "Select Tier"
              ? `${tierOption.replaceAll("Tier", "").trim()} members`
              : "members"
          }.`
        );
        setSuccessOpen(true);
      }

      if (scheduleType === SCHEDULE_NOTIFICATIONS.FUTURE) {
        const { title, message } = notification;
        const scheduledTime = notificationScheduleTime.getTime();
        const currentTime = new Date().getTime();

        if (scheduledTime < currentTime) {
          setError("Scheduled time cannot be in the past");
          return;
        }

        await scheduleNotification({
          scheduleType: "OnceInFuture",
          notificationType: "CustomFilter",
          notificationTitle: title,
          notificationMessage: message,
          notificationScheduleTime: scheduledTime,
          tierOption,
        });
        setTierOption("Select Tier");
        setNotification({ title: null, message: null });
        setSuccessMessage(
          `Success: Custom filter notification scheduled for ${tierOption}.`
        );
        setSuccessOpen(true);
      }

      await refetch();
    } catch (error) {
      setError("Error sending venue flash message");
      console.error("Error sending venue flash message", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (validate()) {
      sendToVenue();
    }
  };

  useEffect(() => {
    handleFetchTiers();
  }, [userStore.selectedVenue]);

  useEffect(() => {
    setError("");
  }, [notification, tierOption]);

  return (
    <div className="bg-white flex items-center justify-center text-center mb-14">
      <div className="px-4 py-5 sm:p-6 w-full max-w-xl">
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          By User Tier Notification
        </h3>
        <div className="mt-2 text-sm text-gray-500">
          <p>Send a notification to users based on their tier level:</p>
        </div>
        <div className="mt-5">
          {loading ? (
            <PEProgressBar />
          ) : (
            <form
              className="w-full mt-5 flex items-center flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className="flex flex-col gap-2 w-full">
                {tierOptions?.length > 0 && (
                  <CustomSelect
                    onChange={(e) => setTierOption(e.target.value)}
                    defaultValue={tierOption}
                  >
                    <option key={"Select Tier"} value={"Select Tier"} disabled>
                      {`Select Tier`}
                    </option>
                    {tierOptions.map((tier) => (
                      <option key={tier} value={tier}>
                        {tier}
                      </option>
                    ))}
                  </CustomSelect>
                )}
              </div>
              <div className="flex flex-col gap-2 w-full">
                <CustomInput
                  onChange={(e) =>
                    setNotification({
                      ...notification,
                      title: e.target.value,
                    })
                  }
                  id="title"
                  name="title"
                  type="title"
                  placeholder="Notification Title"
                />
                <CustomTextarea
                  onChange={(e) =>
                    setNotification({
                      ...notification,
                      message: e.target.value,
                    })
                  }
                  id="message"
                  name="message"
                  type="message"
                  placeholder="Notification Message"
                />
              </div>
            </form>
          )}
          <div className="mt-5">
            {error && <ErrorAlert message={error} />}
            <button
              style={{
                backgroundColor: primary,
              }}
              onClick={handleSubmit}
              type="button"
              className="w-full rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
            >
              Send to{" "}
              {tierOption === `No Tier`
                ? "No Tier Members"
                : tierOption !== "Select Tier"
                ? `${tierOption.replaceAll("Tier", "")} Members`
                : "Members"}
            </button>
          </div>
        </div>
        <div className="mt-5">
          <PhonePreview
            screenType="NOTIFICATION_PREVIEW"
            notificationTitle={notification.title}
            notificationMessage={notification.message}
            userState={userState}
          />
        </div>
        <Snackbar
          open={successOpen}
          autoHideDuration={3000}
          onClose={() => setSuccessOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSuccessOpen(false)}
            severity="success"
            variant="filled"
          >
            {successMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default observer(CustomFilterNotification);
